
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { StepProps } from './types';
import { nigerianStates } from '@/data/nigerianStates';

const PersonalDetailsStep: React.FC<StepProps> = ({ data, onDataChange, onNext, onPrevious, errors }) => {
  const selectedState = nigerianStates.find(state => state.name === data.state);
  const availableLGAs = selectedState ? selectedState.lgas : [];
  const [calendarYear, setCalendarYear] = React.useState(data.dateOfBirth?.getFullYear() || new Date().getFullYear() - 25);

  const handleStateChange = (selectedState: string) => {
    onDataChange({ state: selectedState, localGovernment: '' });
  };

  const handleNext = () => {
    const newErrors: Record<string, string> = {};
    
    if (!data.gender) newErrors.gender = 'Gender is required';
    if (!data.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
    if (!data.state) newErrors.state = 'State is required';
    if (!data.localGovernment) newErrors.localGovernment = 'Local Government Area is required';
    
    if (Object.keys(newErrors).length === 0) {
      onNext();
    }
  };

  const navigateYear = (direction: 'prev' | 'next') => {
    const newYear = direction === 'prev' ? calendarYear - 1 : calendarYear + 1;
    const currentYear = new Date().getFullYear();
    if (newYear >= 1900 && newYear <= currentYear) {
      setCalendarYear(newYear);
    }
  };

  const handleMonthChange = (month: Date) => {
    setCalendarYear(month.getFullYear());
  };

  const currentCalendarDate = new Date(calendarYear, 0, 1);

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="text-center text-2xl font-bold text-gray-900">
          Personal Details
        </CardTitle>
        <p className="text-center text-gray-600">Step 2 of 4</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="gender">Gender *</Label>
            <Select value={data.gender} onValueChange={(value) => onDataChange({ gender: value })}>
              <SelectTrigger className={errors.gender ? 'border-red-500' : ''}>
                <SelectValue placeholder="Select gender" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Male">Male</SelectItem>
                <SelectItem value="Female">Female</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
                <SelectItem value="Prefer not to say">Prefer not to say</SelectItem>
              </SelectContent>
            </Select>
            {errors.gender && <p className="text-red-500 text-sm mt-1">{errors.gender}</p>}
          </div>
          <div>
            <Label htmlFor="dateOfBirth">Date of Birth *</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !data.dateOfBirth && "text-muted-foreground",
                    errors.dateOfBirth && "border-red-500"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {data.dateOfBirth ? format(data.dateOfBirth, "PPP") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <div className="flex items-center justify-between p-3 border-b">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigateYear('prev')}
                    disabled={calendarYear <= 1900}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="font-semibold text-lg">{calendarYear}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigateYear('next')}
                    disabled={calendarYear >= new Date().getFullYear()}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
                <Calendar
                  mode="single"
                  selected={data.dateOfBirth || undefined}
                  onSelect={(date) => onDataChange({ dateOfBirth: date || null })}
                  disabled={(date) => date > new Date() || date < new Date("1900-01-01")}
                  month={currentCalendarDate}
                  onMonthChange={handleMonthChange}
                  initialFocus
                  className={cn("p-3 pointer-events-auto")}
                />
              </PopoverContent>
            </Popover>
            {errors.dateOfBirth && <p className="text-red-500 text-sm mt-1">{errors.dateOfBirth}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="state">State of Residence *</Label>
            <Select value={data.state} onValueChange={handleStateChange}>
              <SelectTrigger className={errors.state ? 'border-red-500' : ''}>
                <SelectValue placeholder="Select your state" />
              </SelectTrigger>
              <SelectContent>
                {nigerianStates.map((state) => (
                  <SelectItem key={state.name} value={state.name}>
                    {state.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.state && <p className="text-red-500 text-sm mt-1">{errors.state}</p>}
          </div>
          <div>
            <Label htmlFor="localGovernment">LGA of Residence *</Label>
            <Select 
              value={data.localGovernment} 
              onValueChange={(value) => onDataChange({ localGovernment: value })}
              disabled={!data.state}
            >
              <SelectTrigger className={errors.localGovernment ? 'border-red-500' : ''}>
                <SelectValue placeholder="Select LGA" />
              </SelectTrigger>
              <SelectContent>
                {availableLGAs.map((lga) => (
                  <SelectItem key={lga} value={lga}>
                    {lga}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.localGovernment && <p className="text-red-500 text-sm mt-1">{errors.localGovernment}</p>}
          </div>
        </div>

        <div>
          <Label htmlFor="ward">Ward (Optional)</Label>
          <Input
            id="ward"
            type="text"
            value={data.ward || ''}
            onChange={(e) => onDataChange({ ward: e.target.value })}
            placeholder="Enter your ward"
          />
        </div>

        <div className="flex gap-4 mt-6">
          <Button onClick={onPrevious} variant="outline" className="flex-1">
            Previous
          </Button>
          <Button onClick={handleNext} className="flex-1">
            Next: Movement Details
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default PersonalDetailsStep;
